import { useEffect, useState } from "react";
import { useModel } from "@rapid/contexts/ModelContext";
import { useGetPredictions } from "@rapid/hooks/useGetPredictions";
import { useFlowState } from "@rapid/hooks/useFlowState";
import { AdjustPredictionsActionCard } from "./AdjustPredictionsActionCard";
import { AnnotationSummaryActionCard } from "./AnnotationSummaryActionCard";
import { DownloadActionCard } from "./DownloadActionCard";
import { ImproveActionCard } from "./ImproveActionCard";
import { useReviewTab } from "../ReviewContext";
import { ReviewTab } from "../ReviewTabs";
import { PublishActionCard } from "./PublishComponents";
import { useSignInAndCreateWorkflow } from "@rapid/hooks/useSignInAndCreateWorkflow";
import { Divider } from "@scripts/react/components/Divider";
import { ActionCard } from "./ActionCard";
import { BuildSteps, Flows } from "@rapid/types/flows";
import { InferenceStatus, useCurrentMedia, useInferenceQueueItem } from "@rapid/store";

const DividerComponent = Divider as any;

interface ReviewActionButtonsProps {
    forceVideoFlow?: boolean;
}

export const ReviewActionButtons = ({ forceVideoFlow = false }: ReviewActionButtonsProps) => {
    const {
        activeTab,
        setActiveTab,
        startEditingModel,
        hasPromptChanges,
        markPromptFingerprintClean
    } = useReviewTab();
    const isAnnotationsView = activeTab === ReviewTab.MyAnnotations;
    const editActionTitle = isAnnotationsView ? "Edit Annotations" : "Edit Predictions";
    const [isRerunningPredictions, setIsRerunningPredictions] = useState(false);

    const { setStep } = useFlowState();
    const { isDownloading, isTestingNewFiles, setIsTestingNewFiles } = useModel();
    const { loading: signInLoading } = useSignInAndCreateWorkflow();
    const currentMedia = useCurrentMedia();
    const inferenceQueueItem = useInferenceQueueItem(currentMedia?.file.id);
    const isInferenceRunning =
        inferenceQueueItem?.status === InferenceStatus.QUEUED ||
        inferenceQueueItem?.status === InferenceStatus.INFERRING;

    // Reset isTestingNewFiles when component mounts (entering Review step)
    useEffect(() => {
        setIsTestingNewFiles(false);
    }, [setIsTestingNewFiles]);

    const isActionDisabled = isDownloading || isTestingNewFiles || signInLoading;

    // UGLY hack, but if there is an error in model generation, we switch back to the prompt step where the error is displayed
    // TODO(japrescott, 2025-09-10): Better error handling not relying on the inference queue state
    const { generateModel, isLoadingInferenceQueue, errorInferenceQueue } = useGetPredictions();
    useEffect(() => {
        if (!isLoadingInferenceQueue && errorInferenceQueue) {
            console.log("WOOOPS, got an error on the inference queue, going back to prompt step", {
                errorInferenceQueue
            });
            setStep({ flow: Flows.BUILD, step: BuildSteps.PROMPT });
        }
    }, [errorInferenceQueue, isLoadingInferenceQueue, setStep]);

    const rerunPredictions = async () => {
        if (isRerunningPredictions) return;

        setActiveTab(ReviewTab.DraftModel);
        setIsRerunningPredictions(true);
        try {
            await generateModel();
            markPromptFingerprintClean();
        } finally {
            setIsRerunningPredictions(false);
        }
    };

    return (
        <>
            <div className="content gap-3">
                <PublishActionCard disabled={isActionDisabled} />
                <ImproveActionCard disabled={isActionDisabled} />
                <DividerComponent text="OR" />
                {!isAnnotationsView && <AdjustPredictionsActionCard disabled={isActionDisabled} />}
                <DownloadActionCard disabled={isActionDisabled} />
                <ActionCard
                    title={editActionTitle}
                    icon="far fa-pen"
                    onClick={isActionDisabled || isInferenceRunning ? undefined : startEditingModel}
                    disabled={isActionDisabled || isInferenceRunning}
                />
                {hasPromptChanges && (
                    <ActionCard
                        title={isRerunningPredictions ? "Rerunning..." : "Rerun Predictions"}
                        icon={isRerunningPredictions ? "far fa-spinner fa-spin" : "far fa-rotate"}
                        onClick={
                            isActionDisabled || isInferenceRunning || isRerunningPredictions
                                ? undefined
                                : rerunPredictions
                        }
                        disabled={isActionDisabled || isInferenceRunning || isRerunningPredictions}
                    />
                )}
                {isAnnotationsView ? (
                    <AnnotationSummaryActionCard disabled={isActionDisabled} />
                ) : null}
            </div>
        </>
    );
};

export const ActionButtons = ReviewActionButtons;
